import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PageShell from '../../components/PageShell';
import { PlanCard } from '../../components/PlanCard';
import { useP } from '../../i18n/useP';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { useRegisterPageExports } from '../../exports/pageExports';
import { useViewExcelExport } from '../../components/ExcelExportButton';
import {
  getCapexPlan, createCapexItem, patchCapexItem, deleteCapexItem,
  getAssetsView, patchAsset, getCapexSchedule, getAssetsSchedule,
} from '../../api/financials_capex';
import MonthYearPicker from '../../components/MonthYearPicker';
import CapexCharts from './CapexCharts';
import AssetsCharts from './AssetsCharts';
import './CapexPlanPage.css';

/* PLAN D'INVESTISSEMENT (2026-09-19) — une vue, tous les exercices.

   Les investissements se saisissaient dans le prévisionnel, par exercice : un
   bien acheté en 2026 n'existait plus quand on ouvrait 2027, et son
   amortissement devait être retapé. Ici, un bien se pose UNE fois, avec sa date
   et sa durée ; le prévisionnel, le budget et la trajectoire y lisent ce qui
   les concerne.

   DEUX ÉTAPES, dans l'ordre où l'on remplit : le parc DÉJÀ LÀ, puis ce qu'on
   PRÉVOIT. Les deux s'amortissent de la même façon ; ce qui les sépare, c'est
   qu'on saisit l'un en regardant son inventaire et l'autre en regardant devant.

   ⚠️ Aucun calcul ici : l'amortissement par année et la valeur nette viennent
   du serveur (`capex_plan_service`). Les refaire à l'écran donnerait deux
   chiffres pour le même bien. */

const MONTHS = ['Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin',
  'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);


/* ⚠️ CES DEUX CHAMPS VIVENT AU NIVEAU DU MODULE (2026-09-19). Définis DANS le
   composant, ils changeaient d'identité à chaque rendu : React démontait la
   table entière au lieu de la réconcilier, et le focus tombait après chaque
   enregistrement — il fallait deux Tab pour passer d'un champ au suivant.

   Le champ SE VOIT (fond bleu très pâle, jamais de cadre : la charte interdit
   d'encadrer une valeur éditable, pas de la poser sur un aplat) et SE LIT (ses
   séparateurs de milliers hors saisie). Il parle l'UNITÉ DE L'ÉCRAN : en kCHF,
   taper 5 enregistre 5'000 ; `raw` le dispense pour ce qui n'est pas un
   montant (une durée, un taux, une année). */
const sep = (n) => Math.round(n).toLocaleString('de-CH').replace(/\u202f|,|\u00a0/g, "'");

function Num({ value, onSave, placeholder = '—', cls = '', raw = false, k = 1, ariaLabel }) {
  const div = raw ? 1 : k;
  const v = Number.isFinite(Number(value)) ? Number(value) : 0;
  return (
    /* La clé suit la valeur et l'unité (2026-09-28) : un champ non contrôlé ne
       relit pas sa valeur de départ, il affichait 20 en CHF comme en kCHF. */
    <input key={`${v}-${div}`} className={`cxp-in ${cls}`} inputMode="decimal" placeholder={placeholder}
           aria-label={ariaLabel}
           defaultValue={v === 0 ? '' : sep(v / div)}
           onFocus={(e) => { e.target.value = v === 0 ? '' : String(v / div); }}
           onBlur={(e) => {
             const n = parseFloat(String(e.target.value).replace(',', '.').replace(/[^0-9.-]/g, ''));
             e.target.value = Number.isFinite(n) ? sep(n) : '';
             onSave(Number.isFinite(n) ? n * div : null);
           }}
           onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
  );
}

/* LE MINI CALENDRIER DE LA CESSION (2026-09-19) : deux années côte à côte,
   l'exercice et le suivant, et les mois CLOS sont éteints — on ne vend pas
   dans un mois déjà arrêté. Une liste de douze mois ne disait pas l'année, et
   la cession ne pouvait se poser que sur l'exercice ouvert. Le panneau se rend
   en PORTAIL : dans la table, qui défile horizontalement, il serait coupé. */
function DatePick({ years, value, minMonth, label, onPick }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btn = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const r = btn.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 6, left: r.left });
    const away = (e) => { if (!e.target.closest('.cxp-cal')) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  const pick = (y, m) => { setOpen(false); onPick(y, m); };
  return (
    <>
      <button type="button" ref={btn} className="cxp-in cxp-dbtn" aria-label={label}
              onClick={() => setOpen((v) => !v)}>
        {value?.month ? `${MONTHS[value.month - 1]} ${value.year}` : '—'}
      </button>
      {open && pos && createPortal(
        <div className="cxp-cal" style={{ top: pos.top, left: pos.left }}>
          <div className="cxp-cal__row">
            {years.map((y, i) => (
              <div key={y}>
                <div className="cxp-cal__t">{y}</div>
                <div className="cxp-cal__g">
                  {MONTHS.map((mo, mi) => (
                    <button key={mo} type="button"
                            className={`cxp-cal__m${value?.year === y && value?.month === mi + 1 ? ' is-on' : ''}`}
                            disabled={i === 0 && mi + 1 <= minMonth}
                            onClick={() => pick(y, mi + 1)}>{mo}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button type="button" className="cxp-cal__none" onClick={() => pick(null, null)}>
            Aucune cession
          </button>
        </div>, document.body)}
    </>
  );
}

/* `only="existing"` (2026-09-27) : Finance garde les ACTIFS EXISTANTS et leurs
   amortissements, dans Données et réglages (2026-09-30). La section du plan
   s'appelle ACQUISITIONS (2026-09-30) : on y pose ce qu'on achète, un flux de
   trésorerie ; les actifs existants sont une règle comptable. `withSummary` :
   la Synthèse (acquisitions et actifs existants) ne se lit que depuis Finance. */
export default function CapexPlanPage({ activeTenant, addToast, year, onYearChange, only = null, withSummary = false }) {
  const p = useP();
  const tid = activeTenant?.id;
  const [plan, setPlan] = useState(null);
  const [unit, setUnit] = usePersistentValue(`capex_unit_${tid}`, 'kchf');
  const [step, setStep] = usePersistentValue(`capex_step_${tid}`, 'new');
  const stepShown = only === 'existing' ? 'existing' : (withSummary && step === 'sum' ? 'sum' : 'new');

  const [assets, setAssets] = useState(null);
  const load = useCallback(() => {
    if (!tid || !year) { setPlan(null); setAssets(null); return; }
    getCapexPlan(tid, year).then((r) => setPlan(r.error ? null : r.data)).catch(() => setPlan(null));
    getAssetsView(tid, year).then((r) => setAssets(r.error ? null : r.data)).catch(() => setAssets(null));
  }, [tid, year]);
  useEffect(() => { load(); }, [load]);

  /* ZOOM SUR UNE ANNÉE (2026-09-19) : la synthèse se lit en projection
     annuelle, une colonne par exercice ; on peut ouvrir UNE année et voir ses
     douze mois. Les deux échéanciers mensuels ne se chargent qu'alors : à quoi
     bon douze mois de chaque exercice quand on en regarde un. */
  const [zoom, setZoom] = usePersistentValue(`capex_zoom_${tid}`, '');
  const [sched, setSched] = useState(null);
  useEffect(() => {
    if (!tid || !zoom) { setSched(null); return undefined; }
    let alive = true;
    Promise.all([getCapexSchedule(tid, Number(zoom)), getAssetsSchedule(tid, Number(zoom))])
      .then(([c, a]) => {
        if (!alive) return;
        setSched({ capex: c && !c.error ? c.data : null, assets: a && !a.error ? a.data : null });
      })
      .catch(() => { if (alive) setSched(null); });
    return () => { alive = false; };
  }, [tid, zoom]);

  /* Un réglage de poste vaut pour UNE année : les mois d'amortissement et la
     cession appartiennent à l'exercice, le taux se pose sur celui qu'on ouvre
     et vaut pour les suivants tant qu'on ne le redit pas. */
  const setAsset = (lineId, y, payload) => patchAsset(tid, lineId, y, payload)
    .then(() => load())
    .catch(() => addToast && addToast(p('Enregistrement impossible'), 'error'));

  /* LA CESSION DÉMÉNAGE AVEC SA DATE : elle vit sur la ligne de SON année, si
     bien que changer d'année la déplace, montant compris. Deux lignes qui en
     portent une seraient deux ventes du même bien. */
  const setDisposal = (po, y, m) => {
    const cur = po.disposal_year || year;
    if (!m) return setAsset(po.line_id, cur, { disposal_month: null, disposal_amount: null });
    if (y === cur) return setAsset(po.line_id, y, { disposal_month: m });
    return patchAsset(tid, po.line_id, cur, { disposal_month: null, disposal_amount: null })
      .then(() => patchAsset(tid, po.line_id, y,
        { disposal_month: m, disposal_amount: po.disposal_amount ?? null }))
      .then(() => load())
      .catch(() => addToast && addToast(p('Enregistrement impossible'), 'error'));
  };

  const k = unit === 'chf' ? 1 : 1000;
  const fmt = (v) => {
    const x = num(v) / k;
    if (Math.abs(x) < 0.005) return '—';
    return x.toLocaleString('de-CH', { maximumFractionDigits: unit === 'chf' ? 0 : 1 });
  };

  const rows = (plan?.rows || []).filter((r) => (r.kind || 'new') === stepShown);
  const years = useMemo(() => plan?.years || [], [plan]);

  const patch = (id, payload) => patchCapexItem(tid, id, payload).then(load)
    .catch(() => addToast && addToast(p('Enregistrement impossible'), 'error'));
  /* LES MOIS CLOS NE SE CHOISISSENT PAS (2026-10-03) : un bien acquis avant
     la clôture est déjà dans la comptabilité, il se saisit dans Actifs
     existants. Une ligne neuve part donc du premier mois ouvert. */
  const lcp = activeTenant?.last_closed_period ? String(activeTenant.last_closed_period).slice(0, 7) : null;
  const closedUntil = lcp ? { year: Number(lcp.slice(0, 4)), month: Number(lcp.slice(5, 7)) } : null;
  const firstOpen = (() => {
    if (!closedUntil) return { year, month: 1 };
    const fo = closedUntil.month === 12 ? { year: closedUntil.year + 1, month: 1 }
      : { year: closedUntil.year, month: closedUntil.month + 1 };
    return fo.year >= year ? fo : { year, month: 1 };
  })();
  const add = () => createCapexItem(tid, {
    label: '', kind: stepShown, acq_year: firstOpen.year, acq_month: firstOpen.month, amount: 0, rate_pct: null,
  }).then(load).catch(() => addToast && addToast(p('Création impossible'), 'error'));
  const remove = (id) => deleteCapexItem(tid, id).then(load).catch(() => {});

  const grossTotal = rows.reduce((s, r) => s + num(r.amount), 0);


  const title = stepShown === 'sum' ? p('Synthèse')
    : (stepShown === 'existing' ? p('Amortissement des comptes') : p('Liste des acquisitions'));

  /* ── ACTIFS EXISTANTS — les POSTES du bilan, pas des biens (2026-09-19).
     L'AMORTISSEMENT SE TAPE À LA MAIN, compte par compte et année par année
     (prototype d'Olivier, 2026-09-30) : c'est la charge que le comptable
     passera, la valeur nette s'en déduit sur le rang du dessous. Le taux, la
     valeur visée et le nombre de mois ne se proposent plus. La cession reste,
     en bout de ligne. */
  const aYears = assets?.years || [];
  const posts = assets?.posts || [];
  const sumY = (key, y) => posts.reduce((s2, po) => s2 + num(po.years?.[y]?.[key]), 0);
  const edge = (i) => (i === 0 ? ' cxa-edge' : '');
  const assetsTable = (
    <div className="lk-hscroll">
      <table className="cxp cxa">
        <thead>
          <tr>
            <th className="cxp-lbl" rowSpan={2}>{p('Compte du bilan')}</th>
            <th className="cxp-n" rowSpan={2}>
              <div>{p('Solde')}</div>
              <div className="cxp-mo">{assets?.closed_count
                ? `31.${String(assets.closed_count).padStart(2, '0')}.${year}`
                : `31.12.${year - 1}`}</div>
            </th>
            <th className="cxa-band cxa-edge" colSpan={aYears.length}>{p('Amortissement de l\'année')}</th>
            <th className="cxa-band cxa-edge" colSpan={2}>{p('Cession')}</th>
          </tr>
          <tr>
            {aYears.map((y, i) => <th key={y} className={`cxp-y${edge(i)}`}>{y}</th>)}
            <th className="cxa-dt cxa-edge">{p('Date')}</th>
            <th className="cxp-y">{p('Prix HT')}</th>
          </tr>
        </thead>
        <tbody>
          {posts.map((po) => (
            <React.Fragment key={po.line_id}>
              <tr className="cxa-acc">
                <td className="cxp-lbl">{po.label}</td>
                <td className="cxp-n">{fmt(po.opening)}</td>
                {aYears.map((y, i) => {
                  const cy = po.years?.[y] || {};
                  return (
                    <td key={y} className={`cxp-y${edge(i)}`}>
                      <Num k={k} value={-num(cy.amort)} cls={cy.manual ? '' : 'cxp-in--auto'}
                           ariaLabel={`${p('Amortissement')} ${po.label} ${y}`}
                           onSave={(v) => setAsset(po.line_id, y,
                             v == null ? { clear_amort: true } : { amort_amount: Math.abs(v) })} />
                    </td>
                  );
                })}
                <td className="cxa-dt cxa-edge">
                  <DatePick years={[year, year + 1]} label={p('Date de cession')}
                            minMonth={assets?.closed_count || 0}
                            value={po.disposal_month ? { year: po.disposal_year || year, month: po.disposal_month } : null}
                            onPick={(y, m) => setDisposal(po, y, m)} />
                </td>
                <td className="cxp-y">
                  <Num k={k} value={po.disposal_amount} ariaLabel={`${p('Prix de cession')} ${po.label}`}
                       onSave={(v) => setAsset(po.line_id, po.disposal_year || year, { disposal_amount: v })} />
                </td>
              </tr>
              <tr className="cxa-nv">
                <td className="cxp-lbl">{p('valeur nette en fin d\'année')}</td>
                <td className="cxp-n" />
                {aYears.map((y, i) => <td key={y} className={`cxp-y${edge(i)}`}>{fmt(po.years?.[y]?.target)}</td>)}
                <td className="cxa-edge" /><td />
              </tr>
            </React.Fragment>
          ))}
          <tr className="cxa-tot">
            <td className="cxp-lbl">{p('Amortissements')}<span className="cxp-to">→ {p('compte de résultat')}</span></td>
            <td className="cxp-n" />
            {aYears.map((y, i) => <td key={y} className={`cxp-y${edge(i)}`}>{fmt(-sumY('amort', y))}</td>)}
            <td className="cxa-edge" /><td />
          </tr>
          <tr className="cxa-tot cxa-tot--b">
            <td className="cxp-lbl">{p('Valeur nette des actifs')}<span className="cxp-to">→ {p('bilan')}</span></td>
            <td className="cxp-n">{fmt(posts.reduce((s2, po) => s2 + num(po.opening), 0))}</td>
            {aYears.map((y, i) => <td key={y} className={`cxp-y${edge(i)}`}>{fmt(sumY('target', y))}</td>)}
            <td className="cxa-edge" /><td />
          </tr>
        </tbody>
      </table>
    </div>
  );

  /* LES EFFETS DU PLAN (2026-09-19, maquette validée) : trois cartes, dans
     l'ordre où un dirigeant les lit — ce qu'il RESTE au bilan, ce que cela
     COÛTE au résultat, ce que cela MOUVEMENTE en trésorerie. Chacune sépare le
     parc existant des nouveaux actifs : les deux ne se règlent pas au même
     endroit, et confondre leurs amortissements empêche de savoir lequel
     corriger.
     Le rang « Gain / perte sur cessions » de la maquette n'est pas repris : le
     plan ne comptabilise aucun produit de cession, il n'en garde que
     l'ENCAISSEMENT, au mois où il tombe (décision du 2026-09-19). */
  const zoomed = !!zoom && !!sched;
  const cols = zoomed ? MONTHS.map((_, i) => i + 1) : years;
  const colLabel = (c) => (zoomed ? MONTHS[c - 1] : String(c));

  const existAmort = (c) => (zoomed
    ? num(sched.assets?.amort?.[String(c)])
    : (assets?.posts || []).reduce((s2, po) => s2 + num(po.years?.[c]?.amort), 0));
  const existNet = (c) => (zoomed
    ? null    // le mois par mois du parc existant n'est pas exposé : l'année suffit
    : (assets?.posts || []).reduce((s2, po) => s2 + num(po.years?.[c]?.target), 0));
  const newAmort = (c) => (zoomed
    ? num(sched.capex?.total?.amort?.[String(c)])
    : (plan?.rows || []).reduce((s2, r) => s2 + num(r.years?.[c]?.amort), 0));
  const newNet = (c) => (zoomed
    ? num(sched.capex?.total?.net?.[String(c)])
    : (plan?.rows || []).reduce((s2, r) => s2 + num(r.years?.[c]?.net), 0));
  const buys = (c) => (zoomed
    ? num(sched.capex?.total?.acquisitions?.[String(c)])
    : (plan?.rows || []).filter((r) => r.acq_year === c).reduce((s2, r) => s2 + num(r.amount), 0));
  const sold = (c) => (zoomed
    ? num(sched.assets?.disposals?.[String(c)])
    : (c === year ? (assets?.posts || []).reduce((s2, po) => s2 + num(po.disposal_amount), 0) : 0));

  /* TROIS CARTES DE PLEIN DROIT (2026-09-19) : elles vivaient dans une carte
     « Synthèse » qui les encadrait une seconde fois, avec un rang numéroté
     pour les distinguer. Trois cartes voisines se distinguent toutes seules, et
     leurs colonnes s'alignent au pixel : la première colonne porte la même
     largeur (`colgroup`), les années se répartissent le reste. */
  const Sheet = ({ head, sub, rows: lines }) => (
    <PlanCard title={head} note={sub}>
      <div className="lk-hscroll">
        <table className="cxs-tbl">
          <colgroup><col className="cxs-c1" />{cols.map((c) => <col key={c} />)}</colgroup>
          <thead>
            <tr>
              <th>{unit === 'chf' ? p('En francs suisses') : p('En milliers de francs suisses')}</th>
              {cols.map((c) => <th key={c} className="cxs-num">{colLabel(c)}</th>)}
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.k} className={l.total ? 'cxs-total' : ''}>
                <td>{l.k}</td>
                {cols.map((c) => (
                  <td key={c} className="cxs-num">{l.v(c) === null ? '—' : fmt(l.v(c))}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PlanCard>
  );

  const summary = (
    <div className="cxs">
      <Sheet head={p('Bilan')}
             sub={zoomed ? p('Valeur nette à la fin de chaque mois') : p('Valeur nette au 31 décembre')}
             rows={[
               { k: p('Actifs existants'), v: existNet },
               { k: p('Nouveaux actifs'), v: newNet },
               { k: p('Total des immobilisations'), total: true,
                 v: (c) => (existNet(c) === null ? newNet(c) : existNet(c) + newNet(c)) },
             ]} />

      <Sheet head={p('Compte de résultat')} sub={p('Amortissements de la période')}
             rows={[
               { k: `${p('Amortissements')} · ${p('actifs existants')}`, v: (c) => -existAmort(c) },
               { k: `${p('Amortissements')} · ${p('nouveaux actifs')}`, v: (c) => -newAmort(c) },
               { k: p('Impact sur le résultat'), total: true, v: (c) => -(existAmort(c) + newAmort(c)) },
             ]} />

      <Sheet head={p('Flux liés aux investissements')} sub={p('Acquisitions et cessions prévues')}
             rows={[
               { k: p('Acquisitions'), v: (c) => -buys(c) },
               { k: p('Prix de vente des actifs cédés'), v: sold },
               { k: p('Flux net'), total: true, v: (c) => sold(c) - buys(c) },
             ]} />
    </div>
  );


  const exp = useViewExcelExport({
    fileBase: p('Plan d\'investissement'), tenantName: activeTenant?.name, year, addToast,
  });
  const exportOptions = useMemo(() => [{ id: 'excel', label: 'Excel', run: exp.run }], [exp.run]);
  useRegisterPageExports(exportOptions);
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);
  const views = useMemo(() => ([{
    id: 'step', kind: 'segmented', label: p('Étape'), value: stepShown, onChange: setStep,
    /* LES INVESTISSEMENTS OUVRENT LE PLAN (2026-09-19) : c'est ce qu'on vient
       décider, quand le parc existant se règle une fois puis se laisse courir. */
    /* « ACQUISITIONS » (demande d'Olivier, 2026-09-28) : l'étape dit ce
       qu'on y pose ; « Investissements » reste le nom de la section. */
    options: [{ id: 'new', label: p('Acquisitions') }, { id: 'sum', label: p('Synthèse') }],
  },
  /* LA FENÊTRE DE LECTURE (2026-09-19) : le costume du plan des ventes, une
     bascule qui ouvre la plage entière ou UN exercice mois par mois. Elle
     pilote les trois feuilles de la synthèse, donc elle vit à l'étage de la
     page ; elle ferme la rangée, puisqu'elle ne paraît que sur la Synthèse. */
  ...(stepShown === 'sum' && years.length ? [{
    id: 'win', kind: 'segmented', label: p('Fenêtre'), value: zoom, onChange: setZoom,
    options: [{ id: '', label: years.length > 1 ? `${years[0]}–${years[years.length - 1]}` : p('Projection') },
      ...years.map((y) => ({ id: String(y), label: String(y) }))],
  }] : [])]), [step, setStep, p, zoom, setZoom, years, stepShown]);
  // Actifs existants seuls, ou Acquisitions sans la Synthèse : rien à choisir.
  const pageViews = only === 'existing' || !withSummary ? null : views;

  return (
    /* Le titre suit la SECTION du menu (Investissements, Parc existant). */
    <PageShell kind="report" title={only === 'existing' ? p('Actifs existants') : p('Acquisitions')} unit={pageUnit}
               views={pageViews} exportRef={exp.ref}>
      {stepShown === 'sum' ? summary : (
      <PlanCard title={title} note={stepShown === 'existing' ? (unit === 'chf' ? 'CHF' : 'kCHF') : `${years[0] || year}–${years[years.length - 1] || year} · ${unit === 'chf' ? 'CHF' : 'kCHF'}`}
                right={null}>
        {stepShown === 'existing' ? assetsTable : (
        <div className="lk-hscroll">
          <table className="cxp cxp--list">
            {/* L'ESPACE SE RÉPARTIT (2026-09-30) : des colonnes déclarées, la
                table prend la largeur de la carte, chaque champ la sienne. */}
            <colgroup>
              <col style={{ width: '24%' }} /><col style={{ width: '15%' }} />
              <col style={{ width: '12%' }} /><col style={{ width: '14%' }} />
              <col style={{ width: '18%' }} /><col style={{ width: '15%' }} />
              <col style={{ width: '2%' }} />
            </colgroup>
            <thead>
              <tr>
                <th className="cxp-lbl">{p('Investissement')}</th>
                <th className="cxp-l">{p('Mois d\'acquisition')}</th>
                {/* L'unité dans l'entête (2026-09-28) : « 20 » ne disait pas s'il
                    s'agissait de francs ou de milliers. */}
                <th className="cxp-n">{p('Montant HT')} {unit === 'chf' ? 'CHF' : 'kCHF'}</th>
                <th className="cxp-l">{p('Méthode')}</th>
                <th className="cxp-l">{p('Durée ou taux')}</th>
                <th className="cxp-l">{p('Début d\'amortissement')}</th>
                <th aria-label={p('Supprimer')} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                /* TROIS MÉTHODES (prototype d'Olivier, 2026-09-30) : LINÉAIRE se
                   dit en DURÉE (5 ans, soit 20 % l'an) ; DÉGRESSIF en TAUX sur la
                   valeur nette ; NON AMORTI n'a ni l'un ni l'autre. Le taux
                   reste la seule donnée stockée : la durée s'en déduit. */
                const meth = r.amort_method || 'linear';
                const years = r.rate_pct ? Math.round((1 / r.rate_pct) * 100) / 100 : null;
                return (
                <tr key={r.id}>
                  <td className="cxp-lbl">
                    <input className="cxp-name" defaultValue={r.label || ''} placeholder={p('Libellé')}
                           onBlur={(e) => patch(r.id, { label: e.target.value })} />
                  </td>
                  {/* UN CALENDRIER POUR LA DATE (prototype d'Olivier, 2026-09-30). */}
                  <td className="cxp-l">
                    <MonthYearPicker month={r.acq_month || 1} year={r.acq_year} canWrite
                                     closedUntil={(r.kind || 'new') === 'new' ? closedUntil : null}
                                     closedTitle={p('Mois clos : un bien déjà comptabilisé se saisit dans Actifs existants')}
                                     ariaLabel={p('Mois d\'acquisition')}
                                     onChange={(m, y) => patch(r.id, { acq_month: m, acq_year: y })} />
                  </td>
                  <td className="cxp-n"><Num k={k} value={r.amount} onSave={(v) => patch(r.id, { amount: v || 0 })} /></td>
                  <td className="cxp-l">
                    <select className="cxp-sel" value={meth} aria-label={p('Méthode')}
                            onChange={(e) => patch(r.id, { amort_method: e.target.value })}>
                      <option value="linear">{p('Linéaire')}</option>
                      <option value="residual">{p('Dégressif')}</option>
                      <option value="none">{p('Non amorti')}</option>
                    </select>
                  </td>
                  <td className="cxp-l">
                    {meth === 'none' ? <span className="cxp-na">—</span> : meth === 'linear' ? (
                      <span className="cxp-pair">
                        <Num raw value={years} placeholder="—"
                             onSave={(v) => patch(r.id, { rate_pct: v ? 1 / v : null })} />
                        <span className="cxp-pct">
                          {p('ans')}{r.rate_pct ? ` · ${Math.round(r.rate_pct * 1000) / 10} % / ${p('an')}` : ''}
                        </span>
                      </span>
                    ) : (
                      <span className="cxp-pair">
                        <Num raw value={r.rate_pct == null ? null : Math.round(r.rate_pct * 1000) / 10}
                             placeholder="—"
                             onSave={(v) => patch(r.id, { rate_pct: v == null ? null : v / 100 })} />
                        <span className="cxp-pct">{p('% de la valeur nette')}</span>
                      </span>
                    )}
                  </td>
                  <td className="cxp-l">
                    {meth === 'none' ? <span className="cxp-na">—</span> : (
                      <MonthYearPicker month={r.amort_start_month || r.acq_month || 1}
                                       year={r.amort_start_year || r.acq_year} canWrite
                                       ariaLabel={p('Début d\'amortissement')}
                                       onChange={(m, y) => patch(r.id, (m === r.acq_month && y === r.acq_year)
                                         ? { clear_amort_start: true }
                                         : { amort_start_month: m, amort_start_year: y })} />
                    )}
                  </td>
                  <td>
                    <button type="button" className="cxp-x" aria-label={p('Supprimer')}
                            onClick={() => remove(r.id)}>×</button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
        {stepShown === 'existing' ? null : (
          /* LE PIED DE LA LISTE (prototype, 2026-09-30) : l'ajout, puis le
             nombre de biens et le total saisi. */
          <div className="cxp-foot cxp-foot--bar">
            <button type="button" className="cxp-new" onClick={add}>＋ {p('Ajouter une ligne')}</button>
            <span>{rows.length} {rows.length > 1 ? p('acquisitions') : p('acquisition')} · {p('total saisi')} {fmt(grossTotal)}</span>
          </div>
        )}
      </PlanCard>
      )}
      {/* LES GRAPHIQUES REMPLACENT LA TABLE DE SYNTHÈSE (2026-09-30). */}
      {stepShown === 'new' && <CapexCharts tid={tid} rows={rows} years={years} unit={unit} />}
      {stepShown === 'existing' && <AssetsCharts posts={posts} years={aYears} unit={unit} />}
      {exp.stage}
    </PageShell>
  );
}
